import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Dropdown, Form, Modal, Pagination, ProgressBar, Spinner, Table } from 'react-bootstrap';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from './useStudyYearSelector';
import { generalClassText } from './leaveOptions';

const PAGE_SIZE = 50;

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api')
  .replace(/\/api\/?$/, '')
  .replace(/\/$/, '');

function StudentAvatar({ photo, name }) {
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;

  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }

  return (
    <span
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        backgroundColor: '#adb5bd',
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initial}
    </span>
  );
}

function scoreVariant(pts) {
  const n = Number(pts);
  if (n < 60) return 'danger';
  if (n < 80) return 'warning';
  return 'success';
}

function scoreLabel(pts) {
  const n = Number(pts);
  if (n < 60) return 'เสี่ยง';
  if (n < 80) return 'ควรปรับปรุง';
  return 'ดี';
}

export default function DisciplineLowConduct() {
  const [skin, setSkin] = useState(localStorage.getItem('skin-mode') ? 'dark' : '');
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector({ defaultToLatest: true });

  const [gender,  setGender]  = useState('');
  const [page,    setPage]    = useState(1);
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [pages,   setPages]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const [selected,      setSelected]      = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState('');

  const openDetail = useCallback(student => {
    if (!yearId) return;

    setSelected(student);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);

    axiosInstance
      .get(`/api/discipline/conduct/${student.st_id}/${yearId}`)
      .then(res => setDetail(res.data || null))
      .catch(() => setDetailError('ไม่สามารถโหลดรายละเอียดการตัดคะแนน'))
      .finally(() => setDetailLoading(false));
  }, [yearId]);

  const closeDetail = useCallback(() => {
    setSelected(null);
    setDetail(null);
    setDetailError('');
  }, []);

  const load = useCallback(() => {
    if (!yearId) return;

    setLoading(true);
    setError('');

    const params = new URLSearchParams({ yearId, page: String(page) });
    if (gender) params.set('gender', gender);

    axiosInstance
      .get(`/api/discipline/conduct/low-score?${params}`)
      .then(res => {
        const data = res.data || {};
        setRows(data.rows || []);
        setTotal(Number(data.pagination?.total || 0));
        setPages(Number(data.pagination?.totalPages || 0));
      })
      .catch(() => {
        setError('ไม่สามารถโหลดรายชื่อนักเรียน');
        setRows([]);
        setTotal(0);
        setPages(0);
      })
      .finally(() => setLoading(false));
  }, [yearId, gender, page]);

  useEffect(() => {
    setPage(1);
    closeDetail();
  }, [yearId, gender, closeDetail]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        <div className="d-sm-flex align-items-center justify-content-between gap-3 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">นักเรียนคะแนนความประพฤติต่ำ</li>
            </ol>
            <h4 className="main-title mb-0">นักเรียนคะแนนความประพฤติต่ำ</h4>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary">
              {yearLabel || 'ปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(y => (
                <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                  ปีการศึกษา {y.year_year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap gap-3 align-items-center">
            <Card.Title as="h6" className="mb-0">
              นักเรียนที่ถูกตัดคะแนน (คะแนนคงเหลือน้อยกว่า 100)
            </Card.Title>
            <Form.Select
              className="ms-sm-auto"
              style={{ width: 180 }}
              value={gender}
              onChange={e => setGender(e.target.value)}
            >
              <option value="">เพศ: ทั้งหมด</option>
              <option value="m">เพศ: ชาย</option>
              <option value="f">เพศ: หญิง</option>
            </Form.Select>
          </Card.Header>

          <Card.Body className="p-0">
            {error && <div className="alert alert-danger m-3">{error}</div>}

            <div className="px-3 pt-3 text-secondary fs-sm">
              ทั้งหมด {total} คน · แสดงหน้าละ {PAGE_SIZE} คน
            </div>

            <div className="table-responsive mt-2">
              <Table hover className={`mb-0 ${skin === 'dark' ? 'table-dark' : ''}`}>
                <thead className={skin === 'dark' ? '' : 'table-secondary'}>
                  <tr>
                    <th>ลำดับ</th>
                    <th>รูป</th>
                    <th>เลขประจำตัว</th>
                    <th>ชื่อ-นามสกุล</th>
                    <th>เพศ</th>
                    <th>ชั้น/ห้อง</th>
                    <th>ชั้นศาสนา</th>
                    <th>หอพัก</th>
                    <th className="text-center">คะแนนถูกตัด</th>
                    <th className="text-center">คะแนนคงเหลือ</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-5">กำลังโหลดข้อมูล...</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center text-secondary py-5">ไม่พบข้อมูลนักเรียน</td>
                    </tr>
                  ) : (
                    rows.map((s, i) => (
                      <tr
                        key={s.st_id}
                        onClick={() => openDetail(s)}
                        style={{ cursor: 'pointer' }}
                        title="ดูรายละเอียดการตัดคะแนน"
                      >
                        <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                        <td>
                          <StudentAvatar photo={s.st_photo} name={s.st_name} />
                        </td>
                        <td>{s.st_idnumber}</td>
                        <td className="fw-semibold">{s.st_name}</td>
                        <td>
                          <Badge bg={s.st_gen === 'm' ? 'primary' : 'danger'}>
                            {s.st_gen === 'm' ? 'ชาย' : 'หญิง'}
                          </Badge>
                        </td>
                        <td>{generalClassText(s) || '-'}</td>
                        <td>
                          {s.islamic_class || '-'}
                          {s.islamic_room_no ? `/${s.islamic_room_no}` : ''}
                        </td>
                        <td>{s.dorm_name || s.dorm_code || '-'}</td>
                        <td className="text-center text-danger">
                          -{s.deducted_points}
                          {Number(s.merit_points) > 0 && (
                            <span className="text-success ms-1">(+{s.merit_points} จิตอาสา)</span>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge bg={scoreVariant(s.remaining_points)}>
                            {s.remaining_points} / 100
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>

          {pages > 1 && (
            <Card.Footer className="d-flex align-items-center justify-content-center gap-3">
              <span className="text-secondary fs-sm">หน้า {page} จาก {pages}</span>
              <Pagination className="mb-0">
                <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)}>
                  ก่อนหน้า
                </Pagination.Prev>
                <Pagination.Next disabled={page === pages} onClick={() => setPage(page + 1)}>
                  ถัดไป
                </Pagination.Next>
              </Pagination>
            </Card.Footer>
          )}
        </Card>

        <Modal show={!!selected} onHide={closeDetail} size="lg" centered scrollable>
          <Modal.Header closeButton>
            <Modal.Title as="h6" className="mb-0">รายละเอียดการตัดคะแนน</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {detailLoading && (
              <div className="text-center py-5">
                <Spinner animation="border" size="sm" className="me-2" />
                กำลังโหลดข้อมูล...
              </div>
            )}

            {!detailLoading && detailError && (
              <div className="alert alert-danger m-3">{detailError}</div>
            )}

            {!detailLoading && !detailError && detail && (
              <>
                <div className="p-3 border-bottom">
                  <div className="d-flex align-items-center gap-3">
                    <StudentAvatar photo={selected.st_photo} name={selected.st_name} />
                    <div className="flex-grow-1">
                      <h5 className="mb-1">{selected.st_name}</h5>
                      <div className="text-secondary fs-sm">
                        {selected.st_idnumber} · ม.{selected.c_id}/{selected.r_no}
                        {selected.p_code ? ` · ${selected.p_code}` : ''}
                      </div>
                    </div>
                    <div className="text-center" style={{ minWidth: 80, flexShrink: 0 }}>
                      <div
                        className={`fw-bold lh-1 text-${scoreVariant(detail.summary.remaining_points)}`}
                        style={{ fontSize: 40 }}
                      >
                        {detail.summary.remaining_points}
                      </div>
                      <div className="text-secondary" style={{ fontSize: 11 }}>/ 100 คะแนน</div>
                      <Badge bg={scoreVariant(detail.summary.remaining_points)} className="mt-1">
                        {scoreLabel(detail.summary.remaining_points)}
                      </Badge>
                    </div>
                  </div>
                  <ProgressBar
                    now={Math.max(0, detail.summary.remaining_points)}
                    variant={scoreVariant(detail.summary.remaining_points)}
                    className="mt-3 rounded-pill"
                    style={{ height: 8 }}
                  />
                  <div className="d-flex justify-content-between mt-1" style={{ fontSize: 11 }}>
                    <span className="text-secondary">
                      ตัดไปแล้ว {detail.summary.deducted_points} คะแนน
                      {Number(detail.summary.merit_points) > 0 && (
                        <span className="text-success ms-1">· จิตอาสา +{detail.summary.merit_points}</span>
                      )}
                    </span>
                    <span className={`fw-semibold text-${scoreVariant(detail.summary.remaining_points)}`}>
                      คงเหลือ {detail.summary.remaining_points} คะแนน
                    </span>
                  </div>
                </div>

                {/* desktop table */}
                <div className="table-responsive d-none d-md-block">
                  <Table hover className={`mb-0 ${skin === 'dark' ? 'table-dark' : ''}`}>
                    <thead className={skin === 'dark' ? '' : 'table-secondary'}>
                      <tr>
                        <th>วันที่</th>
                        <th>รายการ</th>
                        <th>หมายเหตุ</th>
                        <th>ผู้บันทึก</th>
                        <th className="text-center" style={{ width: 90 }}>คะแนน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.records.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-secondary py-4">
                            ยังไม่มีประวัติการตัดคะแนน
                          </td>
                        </tr>
                      ) : (
                        detail.records.map(rec => (
                          <tr key={rec.record_id}>
                            <td className="text-nowrap">
                              {new Date(rec.created_at).toLocaleDateString('th-TH')}
                            </td>
                            <td>{rec.rule_name_snapshot}</td>
                            <td className="text-secondary">{rec.note || '-'}</td>
                            <td className="text-secondary">{rec.created_by_name || '-'}</td>
                            <td className="text-center text-danger fw-semibold">-{rec.deduct_points}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* mobile card rows */}
                <div className="d-md-none">
                  {detail.records.length === 0 ? (
                    <div className="text-center text-secondary py-4">ยังไม่มีประวัติการตัดคะแนน</div>
                  ) : (
                    detail.records.map(rec => (
                      <div key={rec.record_id} className="d-flex align-items-start gap-3 px-3 py-3 border-bottom">
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{rec.rule_name_snapshot}</div>
                          <div className="text-secondary mt-1" style={{ fontSize: 12 }}>
                            {new Date(rec.created_at).toLocaleDateString('th-TH')}
                            {rec.created_by_name ? ` · ${rec.created_by_name}` : ''}
                          </div>
                          {rec.note && (
                            <div className="text-secondary" style={{ fontSize: 12 }}>หมายเหตุ: {rec.note}</div>
                          )}
                        </div>
                        <Badge bg="danger" className="py-2 px-2" style={{ flexShrink: 0 }}>
                          -{rec.deduct_points}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeDetail}>ปิด</Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </>
  );
}
